import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { animate } from "animejs";
import {
  FAMILIES,
  FAMILY_ORDER,
  FEATURED_CHAINS,
  GATEWAY,
  GRPC,
  HEADERS,
  HTTP_STATUSES,
  NETWORKS,
  NF_CODES,
  ERROR_ENVELOPE,
  PLAN_ROWS,
  SDK_SNIPPETS,
  STATS,
  URL_FORMS,
  WEBSOCKET,
  buildCurl,
  networksByFamily,
} from "../../data/apiReference";
import { providersForNetwork } from "../../data/services";
import { networkPath } from "../../lib/networkRoute";
import { Callout, CodeBlock, DocTable, KVCard, Mono, SectionHeading, scrollToId } from "../../Components/api/DocBlocks";
import ChainLogo from "../../Components/api/ChainLogo";
import { implLabel } from "../../Components/api/implMeta";
import { useDemoKey } from "../../lib/demoKey";

const NETWORK_FILTERS = [
  { id: "all", label: "All" },
  { id: "mainnet", label: "Mainnet" },
  { id: "testnet", label: "Testnet" },
];

// Card for one network (chain id) in the catalog grid.
const NetworkCard = ({ network }) => {
  const fam = FAMILIES[network.family];
  const providers = providersForNetwork(network.slug);
  const impls = [...new Set(network.chains.map(implLabel))];
  return (
    <Link
      to={networkPath(network.slug)}
      className="doc-reveal group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 no-underline transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:bg-white/[0.05]"
    >
      <ChainLogo chain={{ name: network.name, logo: network.logo, family: network.family }} size={36} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-white group-hover:text-primary">{network.name}</span>
          <span
            className="shrink-0 rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider"
            style={network.env === "mainnet" ? { color: fam.accent, backgroundColor: `${fam.accent}1F` } : { color: "#9ca3af", backgroundColor: "rgba(255,255,255,0.06)" }}
          >
            {network.label}
          </span>
        </span>
        <span className="block truncate font-mono text-[11px] text-gray-500">
          {network.chainId !== undefined ? `chain id ${String(network.chainId)}` : `x-chain ${network.slug}`}
          <span className="text-gray-600"> · {impls.join(" · ")}</span>
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className="rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: fam.accent, backgroundColor: `${fam.accent}1F` }}>
          {fam.short}
        </span>
        {(providers.nodegate || providers.metrics) && (
          <span className="flex gap-1" aria-label="Also on">
            {providers.nodegate && <span className="h-1.5 w-1.5 rounded-full bg-[#5AA9E6]" title="Nodegate" />}
            {providers.metrics && <span className="h-1.5 w-1.5 rounded-full bg-[#B7E8C8]" title="Chain Graph" />}
          </span>
        )}
      </span>
    </Link>
  );
};

const ApiOverview = () => {
  const location = useLocation();
  const { key } = useDemoKey();
  const [sdk, setSdk] = useState(SDK_SNIPPETS[0].id);
  const [famFilter, setFamFilter] = useState("all");
  const [netFilter, setNetFilter] = useState("all");
  const [q, setQ] = useState("");

  // Sidebar anchor navigation from another route lands here with state.anchor.
  useEffect(() => {
    const anchor = location.state?.anchor;
    if (anchor) {
      const t = setTimeout(() => scrollToId(anchor), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.state]);

  // Cross-fade the SDK code block when the tab changes (anime.js).
  useEffect(() => {
    const el = document.getElementById("sdk-code");
    if (!el) return;
    animate(el, { opacity: [0, 1], translateX: [8, 0], duration: 320, ease: "outQuad" });
  }, [sdk]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return NETWORKS.filter(
      (n) =>
        (famFilter === "all" || n.family === famFilter) &&
        (netFilter === "all" || n.env === netFilter) &&
        (!query ||
          n.fullName.toLowerCase().includes(query) ||
          String(n.chainId ?? "").toLowerCase() === query ||
          n.chains.some((c) => c.slug.includes(query) || c.name.toLowerCase().includes(query) || (c.aliases || []).some((a) => a.includes(query)))),
    );
  }, [famFilter, netFilter, q]);

  const snippet = SDK_SNIPPETS.find((s) => s.id === sdk) || SDK_SNIPPETS[0];

  return (
    <article className="max-w-4xl">
      {/* ---------------- Overview ---------------- */}
      <SectionHeading id="overview" eyebrow="Getting started" title="Overview" n="01">
        Datanodes is Nodefleet's multi-chain RPC gateway. You authenticate once with an API key and pick the network per request —
        the gateway routes to dedicated nodes, meters usage per organization and returns the chain's native response unchanged.
      </SectionHeading>

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        {[
          { t: "One endpoint", d: GATEWAY.rpc.replace("https://", ""), s: "HTTPS for JSON-RPC, REST and GraphQL. Dedicated hosts for WebSocket and gRPC." },
          { t: "One key", d: "x-api-key: sk_live_…", s: "Works on every chain your organization has access to. Create keys per project in the dashboard." },
          { t: "Every interface", d: STATS.interfaces.join(" · "), s: `${STATS.networks} networks (${STATS.chains} slugs) across ${FAMILY_ORDER.length - 1} chain families.` },
        ].map((c) => (
          <div key={c.t} className="doc-reveal rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-primary">{c.t}</p>
            <p className="m-0 mt-2 break-all font-mono text-sm text-white">{c.d}</p>
            <p className="m-0 mt-2 text-sm text-gray-400">{c.s}</p>
          </div>
        ))}
      </div>

      <h3 className="doc-reveal mb-3 text-lg font-bold text-white">Featured references</h3>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        {FEATURED_CHAINS.map((c) => {
          const fam = FAMILIES[c.family];
          return (
            <Link
              key={c.slug}
              to={networkPath(c.slug)}
              className="doc-reveal group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4 no-underline transition-all hover:-translate-y-0.5 hover:border-primary/40"
            >
              <span className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-40 blur-2xl" style={{ backgroundColor: fam.accent }} />
              <ChainLogo chain={c} size={44} />
              <span className="min-w-0 flex-1">
                <span className="block text-base font-bold text-white group-hover:text-primary">{c.name}</span>
                <span className="block text-xs text-gray-400">{fam.interface}</span>
              </span>
              {c.dedicated && <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent">Full guide</span>}
              <span className="text-primary transition-transform group-hover:translate-x-1">→</span>
            </Link>
          );
        })}
      </div>

      {/* ---------------- Authentication ---------------- */}
      <SectionHeading id="authentication" eyebrow="Getting started" title="Authentication" n="02">
        Create a project in the Datanodes dashboard and copy its key (format <Mono>sk_live_…</Mono>). Send it on every request — as a
        header, as gRPC metadata, or embedded in the URL path. Nothing is chain-specific to enable.
      </SectionHeading>
      <Callout title="Public demo key — try it now">
        Every example in this reference runs as-is with the shared demo key{key ? "" : " (loading…)"}: it is a Free-tier, rate-limited key
        for evaluation. For production traffic and higher limits,{" "}
        <a href={GATEWAY.dashboard} target="_blank" rel="noopener noreferrer" className="text-primary">
          create your own key
        </a>
        .
      </Callout>
      <CodeBlock code={`x-api-key: {{KEY}}`} label="Header" />
      <DocTable headers={["Header", "Value", "Notes"]} rows={HEADERS.map((h) => [h.name, h.value, h.note])} mono />

      {/* ---------------- Endpoints ---------------- */}
      <SectionHeading id="endpoints" eyebrow="Getting started" title="Endpoints & headers" n="03">
        Header form and path form are equivalent — the gateway turns path credentials into the same headers before routing. Pick what
        your tooling prefers.
      </SectionHeading>
      <DocTable headers={["Form", "URL pattern", "Notes"]} rows={URL_FORMS.map((u) => [u.label, u.pattern, u.note])} mono />
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <KVCard
          pill="JSON-RPC · REST"
          rows={[
            ["URL", `${GATEWAY.rpc}/`],
            ["x-api-key", "{{KEY}}"],
            ["x-chain", "<slug> — e.g. eth, solana, osmosis-rest"],
            ["Content-Type", "application/json"],
          ]}
        />
        <KVCard
          pill="GraphQL"
          accent="#5AA9E6"
          rows={[
            ["URL", GATEWAY.graphql],
            ["x-api-key", "{{KEY}}"],
            ["x-chain", "sui-graphql"],
            ["Content-Type", "application/json"],
          ]}
        />
        <KVCard
          pill="WebSocket"
          rows={[
            ["URL", GATEWAY.ws],
            ["x-api-key", "{{KEY}}"],
            ["x-chain", WEBSOCKET.chains.join(" · ")],
          ]}
        />
        <KVCard
          pill="gRPC"
          accent="#5AA9E6"
          rows={[
            ["Sui", GATEWAY.suiGrpc],
            ["Solana", GATEWAY.solanaGrpc],
            ["x-api-key", "{{KEY}} (metadata)"],
            ["Chain select", "automatic — from the service path"],
          ]}
        />
      </div>
      <CodeBlock
        label="Your first call — latest Ethereum block"
        code={buildCurl({ slug: "eth", body: '{"jsonrpc":"2.0","id":1,"method":"eth_blockNumber","params":[]}' })}
        response='{ "jsonrpc": "2.0", "id": 1, "result": "0x189a851" }'
      />

      {/* ---------------- Quickstart & SDKs ---------------- */}
      <SectionHeading id="quickstart" eyebrow="Getting started" title="Quickstart & SDKs" n="04">
        Any JSON-RPC / REST client works. Use the header form when you control headers, the path form for URL-only SDKs.
      </SectionHeading>
      <div className="doc-reveal mb-3 flex flex-wrap gap-2" role="tablist" aria-label="SDK">
        {SDK_SNIPPETS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={sdk === s.id}
            onClick={() => setSdk(s.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              sdk === s.id ? "border-primary bg-primary/15 text-primary" : "border-white/10 bg-transparent text-gray-400 hover:border-white/30 hover:text-white"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div id="sdk-code">
        <CodeBlock code={snippet.code} label={snippet.label} />
      </div>
      <Callout title="Switching chains">
        Only <Mono>x-chain</Mono> (or the path segment) changes. For Solana send Solana JSON-RPC methods, for Cosmos chains send Tendermint
        methods, for <Mono>*-rest</Mono> slugs send the REST path — see each network's page for the method catalog.
      </Callout>

      {/* ---------------- Networks ---------------- */}
      <SectionHeading id="networks" eyebrow="Networks" title="Supported networks" n="05">
        {STATS.networks} networks by chain id ({STATS.mainnets} mainnets), grouped by calling convention. Each page lists what Nodefleet runs on
        the network — every Datanodes interface with endpoints, live examples and method catalog, plus Nodegate and Chain Graph where available.
      </SectionHeading>

      <div className="mb-8 grid gap-3 sm:grid-cols-2">
        {FAMILY_ORDER.filter((f) => f !== "other").map((fid) => {
          const fam = FAMILIES[fid];
          const list = networksByFamily(fid);
          const first = list[0];
          return (
            <button
              key={fid}
              type="button"
              onClick={() => {
                setFamFilter(fid);
                scrollToId("chain-grid");
              }}
              className="doc-reveal flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:border-white/30"
            >
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: fam.accent, boxShadow: `0 0 10px ${fam.accent}` }} />
              <span className="min-w-0">
                <span className="block text-sm font-bold text-white">{fam.name}</span>
                <span className="block text-xs text-gray-400">{fam.blurb}</span>
                <span className="mt-2 flex items-center gap-1.5">
                  {list.slice(0, 6).map((n) => (
                    <ChainLogo key={n.slug} chain={{ name: n.name, logo: n.logo, family: n.family }} size={18} />
                  ))}
                  <span className="font-mono text-[11px] text-gray-500">
                    {list.length} network{list.length === 1 ? "" : "s"}
                    {first && first.slug === "sui" ? " · GraphQL + gRPC" : ""}
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div id="chain-grid" className="doc-reveal mb-4 flex scroll-mt-28 flex-wrap items-center gap-2">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name, slug or chain id…"
          className="w-56 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm text-white placeholder-gray-500 outline-none focus:border-primary/50"
        />
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setFamFilter("all")}
            className={`rounded-full border px-2.5 py-1 text-xs ${famFilter === "all" ? "border-primary text-primary" : "border-white/10 text-gray-400 hover:text-white"}`}
          >
            All families
          </button>
          {FAMILY_ORDER.map((fid) => (
            <button
              key={fid}
              type="button"
              onClick={() => setFamFilter(fid)}
              className={`rounded-full border px-2.5 py-1 text-xs ${famFilter === fid ? "border-primary text-primary" : "border-white/10 text-gray-400 hover:text-white"}`}
            >
              {FAMILIES[fid].short}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1 rounded-full border border-white/10 p-0.5">
          {NETWORK_FILTERS.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => setNetFilter(n.id)}
              className={`rounded-full px-2.5 py-1 text-xs ${netFilter === n.id ? "bg-primary/15 text-primary" : "text-gray-400 hover:text-white"}`}
            >
              {n.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-2 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((n) => (
          <NetworkCard key={n.slug} network={n} />
        ))}
      </div>
      <p className="mb-8 text-xs text-gray-500">
        Showing {filtered.length} of {NETWORKS.length} networks. Don't see a chain? <a href={GATEWAY.contact} className="text-primary">Ask us</a> — new networks are added on request.
      </p>

      {/* ---------------- WebSocket ---------------- */}
      <SectionHeading id="websocket" eyebrow="Reference" title="WebSocket" n="06">
        Real-time subscriptions (<Mono>eth_subscribe</Mono> newHeads / logs / pendingTransactions) over a dedicated WebSocket host. Same
        key, same <Mono>x-chain</Mono> header, same per-organization limits.
      </SectionHeading>
      <KVCard pill="WSS" rows={[["URL", WEBSOCKET.url], ["Headers", WEBSOCKET.headers.join(", ")], ["Chains", WEBSOCKET.chains.join(" · ")]]} className="mb-4" />
      <CodeBlock code={WEBSOCKET.example} label="Node.js — ws" />
      <Callout>
        Browser WebSocket clients cannot set custom headers — use a server-side relay or the HTTP JSON-RPC endpoint for browser apps.
      </Callout>

      {/* ---------------- gRPC ---------------- */}
      <SectionHeading id="grpc" eyebrow="Reference" title="gRPC streams" n="07">
        Strongly-typed, low-latency access over HTTP/2. The chain is selected by the service path, so you only send <Mono>x-api-key</Mono>{" "}
        metadata.
      </SectionHeading>
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        {GRPC.map((g) => (
          <div key={g.name} className="doc-reveal rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="m-0 text-sm font-bold text-white">{g.name}</p>
            <p className="m-0 mt-1 break-all font-mono text-xs text-accent">{g.target}</p>
            <p className="m-0 mt-2 text-xs text-gray-400">{g.services}</p>
            <p className="m-0 mt-2 text-xs text-gray-400">{g.note}</p>
            <Link to={g.href} className="mt-3 inline-block text-xs font-semibold text-primary no-underline hover:underline">
              Read the {g.name} reference →
            </Link>
          </div>
        ))}
      </div>

      {/* ---------------- Limits ---------------- */}
      <SectionHeading id="limits" eyebrow="Reference" title="Rate limits & plans" n="08">
        Limits are enforced per organization — per-second RPS and monthly volume — and every API key in the organization draws from the
        same pool. Only successful (2xx) responses count toward your quota.
      </SectionHeading>
      <DocTable headers={["Capability", "Free tier", "Paid tiers"]} rows={PLAN_ROWS} />
      <Callout title="Need more headroom?">
        Upgrade from the{" "}
        <a href={GATEWAY.dashboard} target="_blank" rel="noopener noreferrer" className="text-primary">
          dashboard
        </a>{" "}
        for higher RPS and monthly quota, or{" "}
        <a href={GATEWAY.contact} className="text-primary">
          contact us
        </a>{" "}
        for dedicated nodes, archive depth, streaming and custom SLAs.
      </Callout>

      {/* ---------------- Errors ---------------- */}
      <SectionHeading id="errors" eyebrow="Reference" title="Errors & ref codes" n="09">
        Every error the gateway itself emits is tagged <Mono>[NF] &lt;message&gt; (ref: NF-XXNN)</Mono>. Errors relayed from the chain node
        arrive untouched, so the source is always unambiguous.
      </SectionHeading>
      <CodeBlock code={ERROR_ENVELOPE} label="Gateway error — JSON-RPC envelope" />
      <DocTable headers={["HTTP status", "Meaning"]} rows={HTTP_STATUSES} mono />
      <DocTable headers={["Ref", "Meaning"]} rows={NF_CODES} mono />
      <Callout>
        gRPC surfaces the same conditions as status codes: <Mono>16 UNAUTHENTICATED</Mono>, <Mono>7 PERMISSION_DENIED</Mono>,{" "}
        <Mono>8 RESOURCE_EXHAUSTED</Mono>, <Mono>12 UNIMPLEMENTED</Mono>. GraphQL errors arrive in the standard <Mono>errors[]</Mono> array with an{" "}
        <Mono>extensions.code</Mono>.
      </Callout>
    </article>
  );
};

export default ApiOverview;
